import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import React, { FC, useEffect, useRef, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import HubSpotPagesGrid from "./components/HubSpotPagesGrid";
import PrimaryButton from "../../../components/redesign/elements/Button/PrimaryButton";
import UtmParamsModalRedesign, { UtmParams } from "./components/UtmParamsModalRedesign";
import { CampaignPageTypes } from "../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { getHubspotPages, setCampaignLandingPage } from "../../../redux/reducers/campaignToCreate";

type SourceType = "custom" | "landingPage" | "webPage" | "blogPage";

const PAGE_TYPE_MAP: Record<Exclude<SourceType, "custom">, CampaignPageTypes> = {
  landingPage: CampaignPageTypes.landingPage,
  webPage: CampaignPageTypes.websitePage,
  blogPage: CampaignPageTypes.blogPosts,
};

const URL_REGEX =
  /^(?:ht{2}ps?:\/{2})?(?:[\dA-Za-z-]+\.)+[A-Za-z]{2,}(?::\d+)?(?:\/[\w/-]*)?(?:\.html)?(\?[^\s#]*)?(#\S*)?$/;

const EMPTY_UTM: UtmParams = { campaign: "", content: "", medium: "", source: "", term: "" };

const buildUtmQuery = (p: UtmParams): string => {
  if (!p.campaign && !p.content && !p.medium && !p.source && !p.term) return "";
  const parts = [
    `utm_campaign=${encodeURIComponent(p.campaign.trim())}`,
    `utm_content=${encodeURIComponent(p.content.trim())}`,
    `utm_medium=${encodeURIComponent(p.medium.trim())}`,
  ];
  if (p.source) parts.push(`utm_source=${encodeURIComponent(p.source.trim())}`);
  if (p.term) parts.push(`utm_term=${encodeURIComponent(p.term.trim())}`);
  return parts.join("&");
};

const PanelRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const PillRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  gap: 8,
  alignItems: "center",
});

const pillBase = {
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  padding: "6px 14px",
  cursor: "pointer",
  userSelect: "none" as const,
  transition: "all 0.15s ease",
  lineHeight: 1.4,
};

const DefaultPill = styled("span")(({ theme }: { theme: Theme }) => ({
  ...pillBase,
  background: theme.palette.brand.white,
  color: theme.palette.brand.gray500,
  border: `1px solid ${theme.palette.brand.gray200}`,
  "&:hover": {
    borderColor: theme.palette.brand.gray500,
    color: theme.palette.brand.gray900,
  },
}));

const ActivePill = styled("span")(({ theme }: { theme: Theme }) => ({
  ...pillBase,
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.coral}`,
}));

const FieldsGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
});

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const ButtonRow = styled("div")({
  display: "flex",
  alignItems: "center",
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const RequiredMark = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.coral,
}));

const InputSkin = styled("input")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  padding: "8px 12px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
  "&::placeholder": {
    color: theme.palette.brand.gray500,
  },
  "&:focus": {
    borderColor: theme.palette.brand.blueAccent,
  },
}));

const ErrorText = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  color: theme.palette.brand.errorRed,
}));

const SelectedPageText = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  "& a": {
    color: theme.palette.brand.blueAccent,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const HubSpotSection = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  padding: 16,
}));

const NoHubSpotMessage = styled("p")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  margin: 0,
}));

const LoadingWrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  padding: "24px 0",
});

const LandingPageSelectionPanel: FC = () => {
  const dispatch = useDispatch();

  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const foundHubspotPages = useSelector((state: ReduxApplicationState) => state.campaignToCreate.foundHubspotPages);
  const isPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const storedLandingPage = useSelector((state: ReduxApplicationState) => state.campaignToCreate.landingPage);

  const [selectedSource, setSelectedSource] = useState<SourceType>("custom");
  const [urlValue, setUrlValue] = useState(storedLandingPage || "");
  const [utmParams, setUtmParams] = useState<UtmParams>(EMPTY_UTM);
  const [isUtmModalOpen, setUtmModalOpen] = useState(false);
  const [urlError, setUrlError] = useState("");

  const lastFetchedSource = useRef<string | null>(null);

  const isHubSpotConnected = Boolean(selectedAdvertiser?.hubspot_authed);

  useEffect(() => {
    if (selectedSource !== "custom" && selectedSource !== lastFetchedSource.current) {
      lastFetchedSource.current = selectedSource;
      dispatch(getHubspotPages([PAGE_TYPE_MAP[selectedSource as Exclude<SourceType, "custom">]]));
    }
  }, [selectedSource, dispatch]);

  const buildCombinedUrl = (url: string, params: UtmParams): string => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return "";
    const utmQuery = buildUtmQuery(params);
    return utmQuery ? `${trimmedUrl}?${utmQuery}` : trimmedUrl;
  };

  const validateAndDispatch = (url: string, params: UtmParams) => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      setUrlError("");
      return;
    }
    if (!URL_REGEX.test(trimmedUrl)) {
      setUrlError("Invalid URL format");
      return;
    }
    setUrlError("");
    const combined = buildCombinedUrl(trimmedUrl, params);
    dispatch(setCampaignLandingPage(combined));
  };

  const handleUrlBlur = () => validateAndDispatch(urlValue, utmParams);

  const isUrlValid = URL_REGEX.test(urlValue.trim());

  const handleUtmSave = (params: UtmParams) => {
    setUtmParams(params);
    if (isUrlValid) {
      dispatch(setCampaignLandingPage(buildCombinedUrl(urlValue.trim(), params)));
    }
  };

  const handleHubSpotPageSelect = (url: string) => {
    setUrlValue(url);
    setUtmParams(EMPTY_UTM);
    setUrlError("");
    dispatch(setCampaignLandingPage(url));
  };

  const handlePillClick = (source: SourceType) => {
    setSelectedSource(source);
  };

  const utmQuery = buildUtmQuery(utmParams);
  const previewUrl = urlValue.trim() ? (utmQuery ? `${urlValue.trim()}?${utmQuery}` : urlValue.trim()) : "";
  const displayedLandingPage = previewUrl || storedLandingPage;

  const PILLS: { key: SourceType; label: string }[] = [
    { key: "custom", label: "Custom URL" },
    ...(isHubSpotConnected
      ? ([
          { key: "landingPage", label: "Landing Page" },
          { key: "webPage", label: "Web Page" },
          { key: "blogPage", label: "Blog Page" },
        ] as { key: SourceType; label: string }[])
      : []),
  ];

  return (
    <PanelRoot>
      <PillRow>
        {PILLS.map(({ key, label }) => {
          const Pill = selectedSource === key ? ActivePill : DefaultPill;
          return (
            <Pill key={key} onClick={() => handlePillClick(key)}>
              {label}
            </Pill>
          );
        })}
      </PillRow>

      {!isHubSpotConnected && (
        <NoHubSpotMessage>
          To select a Landing Page, Web Page, or Blog Post from HubSpot, the selected advertiser must have their HubSpot
          account connected.
        </NoHubSpotMessage>
      )}

      <FieldsGrid>
        <FieldGroup>
          <FieldLabel>
            Destination URL <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <InputSkin
            type="text"
            placeholder="https://yoursite.com/page"
            value={urlValue}
            onChange={(e) => {
              const val = e.target.value;
              setUrlValue(val);
              const trimmed = val.trim();
              if (trimmed && URL_REGEX.test(trimmed)) {
                setUrlError("");
                dispatch(setCampaignLandingPage(buildCombinedUrl(trimmed, utmParams)));
              } else if (!trimmed) {
                setUrlError("");
                dispatch(setCampaignLandingPage(""));
              }
            }}
            onBlur={handleUrlBlur}
          />
          <Box style={{ minHeight: 20 }}>{urlError && <ErrorText>{urlError}</ErrorText>}</Box>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>UTM Parameters</FieldLabel>
          <ButtonRow>
            <PrimaryButton disabled={!isUrlValid} onClick={() => setUtmModalOpen(true)}>
              Add UTM Parameters
            </PrimaryButton>
          </ButtonRow>
          <Box style={{ minHeight: 20 }} />
        </FieldGroup>
      </FieldsGrid>

      {displayedLandingPage && (
        <SelectedPageText>
          Destination Page:{" "}
          <a
            href={displayedLandingPage.startsWith("http") ? displayedLandingPage : `http://${displayedLandingPage}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {displayedLandingPage}
          </a>
        </SelectedPageText>
      )}

      {selectedSource !== "custom" && (
        <HubSpotSection>
          {isPending ? (
            <LoadingWrapper>
              <CircularProgress size={24} />
            </LoadingWrapper>
          ) : (
            <HubSpotPagesGrid
              pages={foundHubspotPages}
              selectedUrl={storedLandingPage || ""}
              onSelect={handleHubSpotPageSelect}
            />
          )}
        </HubSpotSection>
      )}

      <UtmParamsModalRedesign
        open={isUtmModalOpen}
        initialValues={utmParams}
        onClose={() => setUtmModalOpen(false)}
        onSave={handleUtmSave}
      />
    </PanelRoot>
  );
};

export default LandingPageSelectionPanel;
